import Layer from '@arcgis/core/layers/Layer';
import WMSLayer from '@arcgis/core/layers/WMSLayer';
import WMTSLayer from '@arcgis/core/layers/WMTSLayer';
import { COMPANION_IMAGE_TITLE } from '../companionLayerTitles';
import type { ResolvedRasterCompanion } from './rasterCompanionTypes';

const WMS_IMAGE_MAX = 512;

/** Default CRG-style ground floor until the level switcher changes it. */
const DEFAULT_STACKING_ORDER = 0;

function defaultWmsLayerParameters(stackingOrder: number) {
  return {
    tiled: 'true',
    CQL_FILTER: `stackingorder=${stackingOrder}`,
  };
}

function imageTitle(index: number, total: number): string {
  return total === 1
    ? COMPANION_IMAGE_TITLE
    : `${COMPANION_IMAGE_TITLE} ${index + 1}`;
}

/**
 * Build companion raster layer(s) for a WFS service.
 * - WMS: one layer with one or more sublayers
 * - WMTS: one layer per activeLayer id (ArcGIS WMTSLayer is single-activeLayer)
 * - Optional secondary-CQL WMS (e.g. MACRO), off by default and toggled in UI
 *
 * Titles are role-based (Image) so Map Layers checkboxes stay readable and
 * don't depend on publisher naming like `FL_Orange_products`.
 */
export function createCompanionRasterLayers(
  companion: ResolvedRasterCompanion,
  options: { id: string; opacity?: number; secondaryOpacity?: number },
): Layer[] {
  if (companion.protocol === 'wmts') {
    return companion.layerNames.map((layerName, index) => {
      const isOnly = companion.layerNames.length === 1;
      return new WMTSLayer({
        id: isOnly ? options.id : `${options.id}-${index}`,
        url: companion.serviceUrl,
        title: imageTitle(index, companion.layerNames.length),
        activeLayer: { id: layerName },
        visible: true,
        ...(companion.customParameters
          ? { customParameters: companion.customParameters }
          : {}),
        ...(options.opacity !== undefined ? { opacity: options.opacity } : {}),
      });
    });
  }

  const layers: Layer[] = [
    new WMSLayer({
      id: options.id,
      url: companion.serviceUrl,
      title: COMPANION_IMAGE_TITLE,
      visible: true,
      imageMaxWidth: WMS_IMAGE_MAX,
      imageMaxHeight: WMS_IMAGE_MAX,
      sublayers: companion.layerNames.map((name, index) => ({
        name,
        title: imageTitle(index, companion.layerNames.length),
      })),
      customLayerParameters: {
        ...defaultWmsLayerParameters(
          companion.defaultStackingOrder ?? DEFAULT_STACKING_ORDER,
        ),
        ...companion.customLayerParameters,
      },
      ...(companion.customParameters
        ? { customParameters: companion.customParameters }
        : {}),
      ...(options.opacity !== undefined ? { opacity: options.opacity } : {}),
    }),
  ];

  const secondaryFilter = companion.secondaryCql?.cql_filter?.trim();
  if (secondaryFilter) {
    const secondaryOpacity =
      options.secondaryOpacity !== undefined
        ? options.secondaryOpacity
        : options.opacity;
    // options.id is `${gisLayerID}-raster` → `${gisLayerID}-raster-secondary`
    layers.push(
      new WMSLayer({
        id: `${options.id}-secondary`,
        url: companion.serviceUrl,
        title: companion.secondaryCql?.title?.trim() || 'Secondary',
        visible: false,
        listMode: 'hide',
        imageMaxWidth: WMS_IMAGE_MAX,
        imageMaxHeight: WMS_IMAGE_MAX,
        sublayers: companion.layerNames.map(name => ({
          name,
          title: companion.secondaryCql?.title?.trim() || 'Secondary',
        })),
        customLayerParameters: {
          tiled: 'true',
          ...companion.customLayerParameters,
          // Secondary CQL must win over any default stackingorder param.
          CQL_FILTER: secondaryFilter,
        },
        ...(companion.customParameters
          ? { customParameters: companion.customParameters }
          : {}),
        ...(secondaryOpacity !== undefined
          ? { opacity: secondaryOpacity }
          : {}),
      }),
    );
  }

  return layers;
}
